import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMutation, useMasterData } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface FishSamplingFormData {
  pond_id: string;
  sampling_date: string;
  sample_weight_kg?: number;  // Optional for multi-species mode
  sample_count?: number;      // Optional for multi-species mode
  fish_species?: string | string[];  // Single species or multi-species
  species_breakdown?: {[key: string]: {weight_kg: number, count: number}};  // Species-specific breakdown
  estimated_total_biomass_kg?: number;
  remarks?: string;
  location_id?: string;
}

interface FishSamplingLog {
  id: string;
  pond_id: string;
  sampling_date: string;
  sample_weight_kg: number;
  sample_count: number;
  avg_weight_g?: number;
  estimated_total_biomass_kg?: number;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondSamplingData {
  id: string;
  name: string;
  pond_type: string;
  sample_weight_kg: string;
  sample_count: string;
  estimated_total_biomass_kg: string;
  remarks: string;
  expanded: boolean;
  selected_species: string[];  // Multi-species selection
  species_breakdown: {[key: string]: {weight_kg: string, count: string}};  // Species-specific breakdown
  cycles?: Array<{
    id: string;
    cycle_name: string;
    fish_species: string;
    growth_health_status: string;
    target_weight_g: number;
    current_weight_g: number;
  }>;
  expectedWeight?: number;
}

const FishSamplingForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [samplingDate, setSamplingDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondSamplingData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { mutate: createFishSamplingLog, loading: creating } = useMutation<FishSamplingLog, FishSamplingFormData>();
  const [cycles, setCycles] = useState<any[]>([]);
  const [speciesInventoryByPond, setSpeciesInventoryByPond] = useState<{[pondId: string]: {[species: string]: any}}>({});
  const [inventoryLoadingByPond, setInventoryLoadingByPond] = useState<{[pondId: string]: boolean}>({});
  
  // Get fish species from master data
  const { data: speciesData } = useMasterData('fish_species', selectedLocation?.id || null);

  // Fetch cycles for the location
  const fetchCycles = useCallback(async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await apiClient.get(`/cycles?location_id=${selectedLocation.id}&season_year=2025`);
      setCycles(response.data);
    } catch (err) {
      console.error('Error fetching cycles:', err);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (ponds) {
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        sample_weight_kg: '',
        sample_count: '',
        estimated_total_biomass_kg: '',
        remarks: '',
        expanded: false,
        selected_species: [],
        species_breakdown: {}
      })));
    }
  }, [ponds]);

  useEffect(() => {
    if (selectedLocation) {
      fetchCycles();
    }
  }, [selectedLocation, fetchCycles]);

  const updatePond = (pondId: string, field: keyof PondSamplingData, value: string | boolean | string[] | {[key: string]: {weight_kg: string, count: string}}) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const handleSpeciesSelection = (pondId: string, species: string, selected: boolean) => {
    setPondsData(pondsData.map(pond => {
      if (pond.id === pondId) {
        let newSelectedSpecies = [...pond.selected_species];
        if (selected) {
          if (!newSelectedSpecies.includes(species)) {
            newSelectedSpecies.push(species);
          }
        } else {
          newSelectedSpecies = newSelectedSpecies.filter(s => s !== species);
        }
        
        // Initialize breakdown for new species
        const newBreakdown = { ...pond.species_breakdown };
        if (selected && !newBreakdown[species]) {
          newBreakdown[species] = { weight_kg: '', count: '' };
        } else if (!selected) {
          delete newBreakdown[species];
        }
        
        return { ...pond, selected_species: newSelectedSpecies, species_breakdown: newBreakdown };
      }
      return pond;
    }));
  };

  const updateSpeciesBreakdown = (pondId: string, species: string, field: 'weight_kg' | 'count', value: string) => {
    setPondsData(pondsData.map(pond => {
      if (pond.id === pondId) {
        const newBreakdown = { ...pond.species_breakdown };
        if (!newBreakdown[species]) {
          newBreakdown[species] = { weight_kg: '', count: '' };
        }
        newBreakdown[species][field] = value;
        return { ...pond, species_breakdown: newBreakdown };
      }
      return pond;
    }));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
    // Lazy load species inventory when expanding a pond for the first time
    const isLoaded = Boolean(speciesInventoryByPond[pondId]);
    const isLoading = Boolean(inventoryLoadingByPond[pondId]);
    if (!isLoaded && !isLoading) {
      void fetchSpeciesInventory(pondId);
    }
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  // Bulk actions for efficiency
  const copyToAllPonds = (field: keyof PondSamplingData, value: string) => {
    setPondsData(pondsData.map(pond => ({ ...pond, [field]: value })));
  };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      sample_weight_kg: '', 
      sample_count: '', 
      estimated_total_biomass_kg: '',
      remarks: '',
      selected_species: [],
      species_breakdown: {}
    })));
  };

  // Fetch species inventory for a pond
  const fetchSpeciesInventory = async (pondId: string) => {
    if (!pondId) return;
    try {
      setInventoryLoadingByPond(prev => ({ ...prev, [pondId]: true }));
      const response = await apiClient.get(`/ponds/${pondId}/species-inventory`);
      const inventory: {[key: string]: any} = {};
      (response.data || []).forEach((item: any) => {
        const key = (item.species || '').toLowerCase();
        inventory[key] = item;
      });
      setSpeciesInventoryByPond(prev => ({ ...prev, [pondId]: inventory }));
    } catch (e) {
      console.error('Error fetching species inventory for pond', pondId, e);
      setSpeciesInventoryByPond(prev => ({ ...prev, [pondId]: {} }));
    } finally {
      setInventoryLoadingByPond(prev => ({ ...prev, [pondId]: false }));
    }
  };

  const calculateAvgWeight = (weight: string, count: string) => {
    const w = parseFloat(weight);
    const c = parseInt(count);
    if (w && c && c > 0) {
      return ((w * 1000) / c).toFixed(1);
    }
    return null;
  };

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => (
      pond.selected_species.length > 0 &&
      pond.selected_species.every(species => {
        const breakdown = pond.species_breakdown[species];
        return breakdown && breakdown.weight_kg && breakdown.count;
      })
    ));
    
    if (validEntries.length === 0) {
      alert('Please fill sampling data for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => {
        const formData: FishSamplingFormData = {
          pond_id: pond.id,
          sampling_date: samplingDate,
          location_id: selectedLocation?.id,
          remarks: pond.remarks || undefined,
        };

        // Multi-species sampling is mandatory; calculate totals from breakdown
        let totalWeight = 0;
        let totalCount = 0;
        const speciesBreakdown: {[key: string]: {weight_kg: number, count: number}} = {};
        
        pond.selected_species.forEach(species => {
          const breakdown = pond.species_breakdown[species];
          if (breakdown && breakdown.weight_kg && breakdown.count) {
            const weight = parseFloat(breakdown.weight_kg);
            const count = parseInt(breakdown.count);
            totalWeight += weight;
            totalCount += count;
            speciesBreakdown[species] = { weight_kg: weight, count: count };
          }
        });

        formData.sample_weight_kg = totalWeight;
        formData.sample_count = totalCount;
        formData.fish_species = pond.selected_species;
        formData.species_breakdown = speciesBreakdown;

        return createFishSamplingLog('/fish-sampling-logs', formData);
      });
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded sampling data for ${validEntries.length} ponds on ${samplingDate}. Cycle projections have been updated automatically.`);
      
      // Refresh cycles to get updated data
      await fetchCycles();
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        sample_weight_kg: '', 
        sample_count: '', 
        estimated_total_biomass_kg: '',
        remarks: '',
        selected_species: [],
        species_breakdown: {}
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPondTotals = (pond: PondSamplingData) => {
    if (pond.selected_species.length > 0) {
      let totalWeight = 0;
      let totalCount = 0;
      pond.selected_species.forEach(species => {
        const breakdown = pond.species_breakdown[species];
        if (breakdown) {
          totalWeight += parseFloat(breakdown.weight_kg) || 0;
          totalCount += parseInt(breakdown.count) || 0;
        }
      });
      return { totalWeight, totalCount };
    }
    return {
      totalWeight: parseFloat(pond.sample_weight_kg) || 0,
      totalCount: parseInt(pond.sample_count) || 0
    };
  };

  const getTotalSampleWeight = () => {
    const total = pondsData.reduce((sum, pond) => sum + getPondTotals(pond).totalWeight, 0);
    return total.toFixed(2);
  };

  const getTotalSampleCount = () => {
    return pondsData.reduce((sum, pond) => sum + getPondTotals(pond).totalCount, 0);
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => (
      pond.selected_species.length > 0 &&
      pond.selected_species.every(species => {
        const br = pond.species_breakdown[species];
        return br && br.weight_kg && br.count;
      })
    )).length;
  };

  // Get cycles for a specific pond
  const getPondCycles = (pondId: string) => {
    return cycles.filter(cycle => cycle.pond_id === pondId && cycle.is_active);
  };

  // Get expected weight for a pond based on active cycles
  const getExpectedWeight = (pondId: string) => {
    const pondCycles = getPondCycles(pondId);
    if (pondCycles.length === 0) return null;
    
    // Use the most recent cycle's current weight as expected
    const latestCycle = pondCycles.sort((a, b) => 
      new Date(b.planned_start_date).getTime() - new Date(a.planned_start_date).getTime()
    )[0];
    
    return latestCycle.current_weight_g;
  };

  // Get expected weight per species for a pond using latest matching cycle
  const getExpectedWeightForSpecies = (pondId: string, species: string) => {
    // Prefer live inventory avg weight if available
    const inv = speciesInventoryByPond[pondId];
    const normalized = (species || '').toLowerCase();
    if (inv && inv[normalized] && typeof inv[normalized].avg_weight_g === 'number' && inv[normalized].avg_weight_g > 0) {
      return inv[normalized].avg_weight_g;
    }
    const pondCycles = getPondCycles(pondId);
    if (pondCycles.length === 0) return null;
    
    const matching = pondCycles
      .filter(cycle => {
        const fs = cycle.fish_species;
        if (Array.isArray(fs)) {
          return fs.some((s: string) => (s || '').toLowerCase() === normalized);
        }
        return ((fs || '') as string).toLowerCase() === normalized;
      })
      .sort((a, b) => new Date(b.planned_start_date).getTime() - new Date(a.planned_start_date).getTime());
    if (matching.length === 0) return getExpectedWeight(pondId);
    return matching[0].current_weight_g;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">📊 Fish Sampling - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={samplingDate}
            onChange={(e) => setSamplingDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-purple-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-purple-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.map((pond) => {
          const totals = getPondTotals(pond);
          const avgWeight = totals.totalWeight && totals.totalCount
            ? ((totals.totalWeight * 1000) / totals.totalCount).toFixed(1)
            : null;
          const pondCycles = getPondCycles(pond.id);
          const expectedWeight = getExpectedWeight(pond.id);
          return (
            <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Pond Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                    <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                  </div>
                  <button
                    onClick={() => togglePondExpansion(pond.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Active Cycles Information */}
              {pondCycles.length > 0 && (
                <div className="p-4 bg-blue-50 border-b border-blue-100">
                  <div className="text-xs text-blue-600 font-medium mb-2">📊 Active Cycles</div>
                  <div className="space-y-1">
                    {pondCycles.map(cycle => (
                      <div key={cycle.id} className="text-sm text-blue-700 flex items-center justify-between">
                        <span>{cycle.cycle_name} - {cycle.fish_species}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          cycle.growth_health_status === 'ahead' ? 'bg-green-100 text-green-700' :
                          cycle.growth_health_status === 'on_track' ? 'bg-blue-100 text-blue-700' :
                          cycle.growth_health_status === 'behind' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {cycle.growth_health_status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entry Fields */}
              <div className="p-4 space-y-3">
                {pond.selected_species.length === 0 ? (
                  <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
                    Select at least one species to record sampling. Per-species inputs will appear below.
                  </div>
                ) : (
                  <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">
                    Using per-species inputs. Totals auto-calculated: {totals.totalWeight.toFixed(2)} kg, {totals.totalCount} fish.
                  </div>
                )}

                {/* Multi-Species Selection */}
                <div className="border-t border-gray-100 pt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Species Selection (Required)
                  </label>
                  <div className="text-xs text-gray-500 mb-2">
                    Select one or more species to record sampling for this pond
                  </div>
                  
                  {speciesData && speciesData.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {speciesData.map((species: any) => (
                        <label key={species.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pond.selected_species.includes(species.name)}
                            onChange={(e) => handleSpeciesSelection(pond.id, species.name, e.target.checked)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {species.name.replace('_', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Species Breakdown */}
                  {pond.selected_species.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-700">Species Breakdown</div>
                      {pond.selected_species.map(species => {
                        const breakdown = pond.species_breakdown[species] || { weight_kg: '', count: '' };
                        const speciesAvgWeight = breakdown.weight_kg && breakdown.count ? 
                          ((parseFloat(breakdown.weight_kg) * 1000) / parseInt(breakdown.count)).toFixed(1) : null;
                        const liveSpeciesWeight = getExpectedWeightForSpecies(pond.id, species);
                        const inv = speciesInventoryByPond[pond.id];
                        const invItem = inv ? inv[(species || '').toLowerCase()] : undefined;
                        
                        return (
                          <div key={species} className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm font-medium text-gray-700 mb-2 capitalize">
                              {species.replace('_', ' ')}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Weight (kg)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={breakdown.weight_kg}
                                  onChange={(e) => updateSpeciesBreakdown(pond.id, species, 'weight_kg', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Count (fish in sample)</label>
                                <input
                                  type="number"
                                  value={breakdown.count}
                                  onChange={(e) => updateSpeciesBreakdown(pond.id, species, 'count', e.target.value)}
                                  placeholder="0"
                                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                            {speciesAvgWeight && (
                              <div className="mt-2 text-xs text-green-600">
                                Avg Weight: {speciesAvgWeight}g
                              </div>
                            )}
                            {(typeof liveSpeciesWeight === 'number' || invItem) && (
                              <div className="mt-1 text-xs text-gray-500">
                                Live avg weight: {typeof liveSpeciesWeight === 'number' ? Math.round(liveSpeciesWeight) : (invItem && invItem.avg_weight_g ? Math.round(invItem.avg_weight_g) : '—')}g
                                {invItem && typeof invItem.count === 'number' && (
                                  <span className="ml-2">(live count: {invItem.count})</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Auto-calculated Average Weight */}
                {avgWeight && (
                  <div className="bg-green-50 p-2 rounded-lg">
                    <div className="text-xs text-green-600 font-medium">Average Weight (Auto-calculated)</div>
                    <div className="text-sm font-bold text-green-700">{avgWeight} g</div>
                  </div>
                )}

                {/* Growth Performance Comparison */}
                {avgWeight && expectedWeight && (
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="text-xs text-yellow-600 font-medium mb-2">📈 Growth Performance</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-yellow-700">Expected:</span>
                        <span className="font-semibold text-yellow-800 ml-1">{Math.round(expectedWeight)}g</span>
                      </div>
                      <div>
                        <span className="text-yellow-700">Actual:</span>
                        <span className="font-semibold text-yellow-800 ml-1">{avgWeight}g</span>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      {parseFloat(avgWeight) > expectedWeight ? (
                        <span className="text-green-600 font-medium">📈 Ahead of target</span>
                      ) : parseFloat(avgWeight) < expectedWeight * 0.9 ? (
                        <span className="text-red-600 font-medium">📉 Behind target</span>
                      ) : (
                        <span className="text-blue-600 font-medium">✅ On track</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Expanded Fields */}
                {pond.expanded && (
                  <div className="space-y-3 border-t border-gray-100 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Estimated Total Biomass (kg)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={pond.estimated_total_biomass_kg}
                        onChange={(e) => updatePond(pond.id, 'estimated_total_biomass_kg', e.target.value)}
                        placeholder="0.0"
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">Projected total weight of all fish in pond</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">
                          Remarks (Optional)
                        </label>
                        <button
                          onClick={() => handleVoiceInput(pond.id)}
                          className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                        >
                          <span className="text-sm">🎤</span>
                        </button>
                      </div>
                      <textarea
                        value={pond.remarks}
                        onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                        placeholder="Health condition, size uniformity, sampling method, etc."
                        rows={2}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <div className="text-xs text-purple-600 font-medium">Total Sample Weight</div>
            <div className="text-lg font-bold text-purple-700">{getTotalSampleWeight()} kg</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-xs text-blue-600 font-medium">Total Sample Count</div>
            <div className="text-lg font-bold text-blue-700">{getTotalSampleCount()}</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Sampling ({getFilledPondsCount()}/{pondsData.length})
        </button>

        {/* Link to Cycle Planning */}
        <button
          onClick={() => window.open('/cycle-planning', '_blank')}
          className="mt-3 w-full bg-blue-600 text-white py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          <span>📊</span>
          View Cycle Planning Dashboard
        </button>
      </div>
    </div>
  );
};

export default FishSamplingForm;